import { PinterestIcon } from 'react-share';
import { useRecoilValue } from 'recoil';
import { userPinterestStateAtom } from '../../social/atom';
import { useState } from 'react';
import AddPinterestDialog from '../../social/AddPinterestDialog';
import { LoadingButton, TabContext, TabList, TabPanel } from '@mui/lab';
import { Pinterest } from '@mui/icons-material';
import PublicationTimeChooser from './PublicationTImeChooser';
import useSettings from 'app/hooks/useSettings';
import { toast } from 'react-toastify';
import { getFunctions, httpsCallable } from 'firebase/functions';
import useAuth from 'app/hooks/useAuth';
import { CredentialNameWithImage } from 'app/views/credential';

const {
  Stack,
  Typography,
  Card,
  Divider,
  Grid,
  Select,
  MenuItem,
  Tab,
  FormControlLabel,
  Switch,
  TextField,
  Autocomplete,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent
} = require('@mui/material');
const { default: Loading } = require('app/components/MatxLoading');

const LanguageOptions = ['French', 'English', 'Spanish', 'German', 'Italian'];
const ToneOptions = [
  'Warm',
  'Welcoming',
  'Enthusiastic',
  'Neutral',
  'Serious',
  'Humorous',
  'Optimistic',
  'Pessimistic',
  'Empathetic',
  'Authoritative',
  'Inspiring',
  'Passionate'
];
const NarrativeStyleOptions = [
  'Descriptive',
  'Persuasive',
  'Narrative',
  'Informative',
  'Creative',
  'Technical'
];

const GeneralTab = ({ pinterests, site, updateSite, handleAddPinterest, callSave }) => {
  console.log('site', site.pinterest_settings);
  const [isEnablingPinterest, setIsEnablingPinterest] = useState(false);
  const [showError, setShowError] = useState(false);
  const { user } = useAuth();
  const handleEnablePinterest = async (e, v) => {
    // console.log(e, v);
    if (!site?.pinterest_settings.siteId) {
      toast.error('Please assign a Pinterest account to enable Pinterest');
      return;
    }

    if (
      !site?.pinterest_settings.brand ||
      !site?.pinterest_settings.description ||
      !site?.pinterest_settings.CTA
    ) {
      setShowError(true);
      toast.error('Please fill in all the required fields');
      return;
    }
    setIsEnablingPinterest(true);
    try {
      await updateSite({ pinterest_settings: { enabled: e.target.checked } });
      const fn = httpsCallable(getFunctions(), 'pinterestRequest');
      await fn({ site, command: e.target.checked ? 'enable' : 'disable' });
      await callSave();
    } catch (err) {
      console.log(err);
      await updateSite({ pinterest_settings: { enabled: !e.target.checked } });
      toast.error('Failed to ' + (e.target.checked ? 'enable' : 'disable') + ' Pinterest');
    }

    setIsEnablingPinterest(false);
  };

  const updateBoardSettings = (name, value) => {
    let board = { ...site?.pinterest_settings.board };
    board[name] = value;
    if (!(board.use_post_category || board.use_post_tag || board.use_brand_name)) {
      toast.error('You need to select at least one board creation option');
      return;
    }

    updateSite({
      pinterest_settings: {
        board
      }
    });
  };

  return (
    <Grid container spacing={2} justifyContent="space-between" padding={'10px'}>
      <Dialog open={isEnablingPinterest}>
        <DialogTitle>
          {site?.pinterest_settings.enabled ? 'Enabling Pinterest' : 'Disabling Pinterest'}
        </DialogTitle>
        <DialogContent>
          <Stack direction="row" margin={2} justifyContent={'center'} alignItems={'center'}>
            <Loading />
          </Stack>
        </DialogContent>
      </Dialog>
      <Grid item xs={12} md={6}>
        <Typography variant="body1" color="GrayText" marginBottom="5px">
          Enable or disable auto posting to Pinterest.
        </Typography>
        <FormControlLabel
          control={
            <Switch
              disabled={isEnablingPinterest}
              checked={site?.pinterest_settings.enabled}
              onChange={handleEnablePinterest}
            />
          }
          label={site?.pinterest_settings.enabled ? 'Enabled' : 'Disabled'}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <Typography variant="body1" color="GrayText" marginBottom="5px">
          Assign Pinterest accounts to your site to automatically post images to Pinterest.
        </Typography>
        <Select
          size="small"
          fullWidth
          value={site?.pinterest_settings.siteId}
          onChange={(e) => {
            updateSite({ pinterest_settings: { siteId: e.target.value } });
          }}
        >
          {pinterests.map((pinterest, index) => (
            <MenuItem key={index} value={pinterest.id}>
              {pinterest.name}
            </MenuItem>
          ))}
          {user?.plan?.social?.pinterest_acc_limit &&
            pinterests.length < user?.plan?.social?.pinterest_acc_limit && (
              <MenuItem value={''} onClick={handleAddPinterest}>
                + Add Pinterest
              </MenuItem>
            )}
        </Select>
      </Grid>

      <Grid item xs={12}>
        <Typography variant="h6">Boards Settings</Typography>
        <Typography variant="body1" color="GrayText" marginBottom="5px">
          Select boards that you want to post to.
        </Typography>
      </Grid>
      <Grid item xs={12} md={6}>
        <div>
          <FormControlLabel
            control={
              <Checkbox
                checked={site?.pinterest_settings.board.use_post_category}
                onChange={(e) => updateBoardSettings('use_post_category', e.target.checked)}
              />
            }
            label="Use Post Category"
          />

          {site?.pinterest_settings.board.use_post_category && (
            <Typography variant="body1" color="GrayText">
              We will create boards matching your website's categories. The pin will be saved to the
              categories that it belongs to
            </Typography>
          )}
        </div>
      </Grid>
      <Grid item xs={12} md={6}>
        <div>
          <FormControlLabel
            control={
              <Checkbox
                checked={site?.pinterest_settings.board.use_post_tag}
                onChange={(e) => updateBoardSettings('use_post_tag', e.target.checked)}
              />
            }
            label="Use Post Tag"
          />

          {site?.pinterest_settings.board.use_post_tag && (
            <Typography variant="body1" color="GrayText">
              We will create boards matching your website's tags. The pin will be saved to the tags
              that it belongs to
            </Typography>
          )}
        </div>
      </Grid>

      <Grid item xs={12} md={6}>
        <div>
          <FormControlLabel
            control={
              <Checkbox
                checked={site?.pinterest_settings.board.use_brand_name}
                onChange={(e) => updateBoardSettings('use_brand_name', e.target.checked)}
              />
            }
            label="Use Brand Name"
          />

          {site?.pinterest_settings.board.use_brand_name && (
            <Typography variant="body1" color="GrayText">
              We will create a board matching the brand name you provide below. Make sure you do not
              delete the board matching the brand name
            </Typography>
          )}
        </div>
      </Grid>

      <Grid item xs={12} style={{ margin: '5px 0' }}>
        <Typography variant="h6" marginBottom="5px">
          Branding
        </Typography>
        <Typography variant="body1" color="GrayText" marginBottom="5px">
          Customize the branding of your Pinterest posts.
        </Typography>
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          label="Brand Name"
          size="small"
          error={showError && !site?.pinterest_settings.brand}
          fullWidth
          value={site?.pinterest_settings.brand}
          onChange={(e) => updateSite({ pinterest_settings: { brand: e.target.value } })}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          label="Description"
          error={showError && !site?.pinterest_settings.description}
          size="small"
          fullWidth
          value={site?.pinterest_settings.description}
          onChange={(e) => updateSite({ pinterest_settings: { description: e.target.value } })}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          label="Hashtag"
          size="small"
          fullWidth
          value={site?.pinterest_settings.hashtag}
          onChange={(e) => updateSite({ pinterest_settings: { hashtag: e.target.value } })}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          label="Call to Action"
          size="small"
          error={showError && !site?.pinterest_settings.CTA}
          fullWidth
          value={site?.pinterest_settings.CTA}
          onChange={(e) => updateSite({ pinterest_settings: { CTA: e.target.value } })}
        />
      </Grid>
    </Grid>
  );
};

const EditorialGuideTab = ({ site, updateSite, callSave }) => {
  const [triggerNewCreation, setTriggerNewCreation] = useState(false);
  const handleCreatePin = async () => {
    setTriggerNewCreation(true);
    try {
      const fn = httpsCallable(getFunctions(), 'pinterestRequest');
      await callSave();
      await fn({ id: site.id, command: 'createPinRightNow' });
      toast.success('Pin creation task is queued');
    } catch (err) {
      console.log(err);
      toast.error('Failed to create Pin');
    }
    setTriggerNewCreation(false);
  };
  return (
    <Grid container spacing={3} justifyContent="space-between" padding={'10px'}>
      <Grid item xs={12} style={{ margin: '5px 0' }}>
        <Typography variant="h6" marginBottom="5px">
          Editorial Guide for the AI
        </Typography>
        <Typography variant="body1" color="GrayText" marginBottom="5px">
          Customize the AI's writing style for Pinterest posts.
        </Typography>
      </Grid>

      <Grid item xs={12} md={6}>
        <FormControlLabel
          control={
            <Checkbox
              checked={site?.pinterest_settings.emojies}
              onChange={(e) => updateSite({ pinterest_settings: { emojies: e.target.checked } })}
            />
          }
          label="Use Emojies"
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <FormControlLabel
          control={
            <Checkbox
              checked={site?.pinterest_settings.hashtags}
              onChange={(e) => updateSite({ pinterest_settings: { hashtags: e.target.checked } })}
            />
          }
          label=" Use Hashtags"
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <Autocomplete
          freeSolo
          value={site?.pinterest_settings.language}
          onChange={(e, value) => updateSite({ pinterest_settings: { language: value } })}
          options={LanguageOptions}
          getOptionLabel={(option) => option || ''}
          renderInput={(params) => <TextField {...params} label="Language" size="small" />}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          select
          label="Tone"
          size="small"
          fullWidth
          value={site?.pinterest_settings.tone}
          onChange={(e) => updateSite({ pinterest_settings: { tone: e.target.value } })}
        >
          {ToneOptions.map((tone, index) => (
            <MenuItem key={index} value={tone}>
              {tone}
            </MenuItem>
          ))}
        </TextField>
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          select
          size="small"
          label="Narrative Style"
          fullWidth
          value={site?.pinterest_settings.style}
          onChange={(e) => updateSite({ pinterest_settings: { style: e.target.value } })}
        >
          {NarrativeStyleOptions.map((style, index) => (
            <MenuItem key={index} value={style}>
              {style}
            </MenuItem>
          ))}
        </TextField>
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          select
          size="small"
          label="Address Audience"
          fullWidth
          value={site?.pinterest_settings.address}
          onChange={(e) => updateSite({ pinterest_settings: { address: e.target.value } })}
        >
          <MenuItem value="Directly">Directly</MenuItem>
          <MenuItem value="Indirectly">Indirectly</MenuItem>
        </TextField>
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          select
          size="small"
          label="Voice"
          fullWidth
          value={site?.pinterest_settings.voice}
          onChange={(e) => updateSite({ pinterest_settings: { voice: e.target.value } })}
        >
          <MenuItem value="First Person">First Person</MenuItem>

          <MenuItem value="Third Person">Third Person</MenuItem>
        </TextField>
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          label="Text Length (Words)"
          size="small"
          fullWidth
          value={site?.pinterest_settings.text_length}
          onChange={(e) => updateSite({ pinterest_settings: { text_length: e.target.value } })}
        />
      </Grid>

      <Grid item xs={12}>
        <TextField
          label="Note to AI"
          placeholder="Write a note to the AI to guide it in creating Pinterest posts"
          multiline
          rows={3}
          size="small"
          fullWidth
          value={site?.pinterest_settings.note}
          onChange={(e) => updateSite({ pinterest_settings: { note: e.target.value } })}
        />
      </Grid>

      <Grid item xs={12}>
        <LoadingButton
          variant="contained"
          onClick={handleCreatePin}
          loading={triggerNewCreation}
          startIcon={<Pinterest />}
        >
          Create a Pin right now
        </LoadingButton>
      </Grid>
    </Grid>
  );
};

function getStyles(value, values) {
  return {
    fontWeight: values.indexOf(value) === -1 ? 400 : 700
  };
}

const VisualTab = ({ site, updateSite, userData }) => {
  const { settings } = useSettings();
  return (
    <Stack
      sx={{ width: { xs: '100%', xl: '50%' } }}
      justifyContent="space-between"
      padding={'10px'}
      gap={2}
    >
      <Stack style={{ margin: '5px 0' }}>
        <Typography variant="h6" marginBottom="5px">
          Visual
        </Typography>
        <Typography variant="body1" color="GrayText" marginBottom="5px">
          Customize the image style of your Pins
        </Typography>
      </Stack>

      <Stack>
        <TextField
          select
          size="small"
          label="Image Aspect Ratio"
          fullWidth
          value={site?.pinterest_settings.image_ratio}
          onChange={(e) => updateSite({ pinterest_settings: { image_ratio: e.target.value } })}
        >
          <MenuItem value="1:1">1:1</MenuItem>
          <MenuItem value="3:2">3:2</MenuItem>
          <MenuItem value="2:3">2:3</MenuItem>
          <MenuItem value="16:9">16:9</MenuItem>
          <MenuItem value="9:16">9:16</MenuItem>
        </TextField>
      </Stack>

      <Stack style={{ margin: '5px 0 0 0' }}>
        <Typography variant="h6">Select Presets</Typography>
        <Typography variant="body1" color="GrayText" marginBottom="5px">
          Choose from a list of presets to apply to your Pins' images. We will alternate between
          these if multiple presets are selected.
        </Typography>

        <Select
          select
          size="small"
          fullWidth
          style={{ maxWidth: '600px' }}
          multiple
          value={site?.pinterest_settings.presets}
          onChange={(e) =>
            updateSite((p) => ({
              ...p,
              pinterest_settings: { ...p.pinterest_settings, presets: e.target.value }
            }))
          }
        >
          {settings?.presets.map((preset, index) => (
            <MenuItem
              key={preset.id}
              value={preset.id}
              style={getStyles(preset.id, site?.pinterest_settings.presets)}
            >
              {preset.name}
            </MenuItem>
          ))}
        </Select>
      </Stack>

      <Stack style={{ margin: '5px 0 0 0' }}>
        <Typography variant="h6">Select Image Generation Credential</Typography>
        <Typography variant="body1" color="GrayText" marginBottom="5px">
          Images will be generated using this credential for this site.
        </Typography>

        <Select
          select
          size="small"
          fullWidth
          style={{ maxWidth: '600px' }}
          value={site?.pinterest_settings.selected_dc_cred ?? site.settings.selected_dc_cred}
          onChange={(e) => {
            updateSite({
              pinterest_settings: { selected_dc_cred: parseInt(e.target.value) }
            });
          }}
        >
          {userData?.dc_creds?.map((key, i) => (
            <MenuItem key={key.name} value={i}>
              <CredentialNameWithImage cred={key} />
            </MenuItem>
          ))}
        </Select>
      </Stack>
    </Stack>
  );
};

const PublicationTimeTab = ({ site, updateSite }) => {
  return (
    <Grid container spacing={3} justifyContent="space-between" padding={'10px'}>
      <Grid item xs={12} style={{ margin: '5px 0 0 0' }}>
        <Typography variant="h6" marginBottom="5px">
          Publication Time
        </Typography>
        <Typography variant="body1" color="GrayText" marginBottom="5px">
          Control the publication time of your Pinterest posts. Changes in schedules will only be in
          effect after the next pin is generated.
        </Typography>
        <Typography variant="body1" color="GrayText" marginBottom="5px">
          Make sure you press <b>"save changes"</b> after making any changes to the publication
          time.
        </Typography>
      </Grid>
      <Grid item xs={12} md={6}>
        <PublicationTimeChooser site={site} updateSite={updateSite} siteKey="pinterest_settings" />
      </Grid>

      <Grid item xs={12}>
        <Typography variant="h6" marginBottom="5px">
          Recycle Posts
        </Typography>
        <Typography variant="body1" color="GrayText" marginBottom="5px">
          If no new articles are published on this site, we will use the most recent articles to
          generate Pins, provided that the recycling option is enabled, within the limit of the
          number of reuses defined below. If recycling is disabled, the system will select the last
          5 published articles and look for an article that is still unused to create a Pin. If no
          available articles are found, no new Pin will be generated.
        </Typography>
      </Grid>
      <Grid item xs={12} md={6}>
        <FormControlLabel
          control={
            <Checkbox
              checked={site?.pinterest_settings.recycle_post}
              onChange={(e) =>
                updateSite({ pinterest_settings: { recycle_post: e.target.checked } })
              }
            />
          }
          label="Recycle Posts"
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          label="Number of Recycles"
          size="small"
          fullWidth
          disabled={!site?.pinterest_settings.recycle_post}
          value={site?.pinterest_settings.number_of_recycles}
          onChange={(e) =>
            updateSite({ pinterest_settings: { number_of_recycles: e.target.value } })
          }
        />
      </Grid>
    </Grid>
  );
};

const PinterestSettings = ({ site, updateSite, callSave, userData }) => {
  const pinterests = useRecoilValue(userPinterestStateAtom);
  const [openAddPinterest, setOpenAddPinterest] = useState(false);
  const [selectedTab, setSelectedTab] = useState('general');

  const handleAddPinterest = (e) => {
    e.preventDefault();
    setOpenAddPinterest(true);
  };

  if (!site) {
    return (
      <Stack marginTop="12px">
        <Loading />
      </Stack>
    );
  }
  // console.log('site', site);

  return (
    <Card>
      <AddPinterestDialog open={openAddPinterest} onClose={() => setOpenAddPinterest(false)} />
      <Typography
        variant="h6"
        sx={{
          color: '#E60023',
          padding: '12px 16px',
          fontWeight: 500,
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}
      >
        <Pinterest />
        Auto Post to Pinterest
      </Typography>
      <Divider />

      <TabContext value={selectedTab}>
        <TabList
          onChange={(e, v) => {
            setSelectedTab(v);
          }}
        >
          <Tab label="General" value={'general'} />
          <Tab label="Editorial Guide" value={'editorial'} />
          <Tab label="Publication Time" value={'publication'} />
          <Tab label="Visual" value={'visual'} />
        </TabList>

        <TabPanel value={'general'}>
          <GeneralTab
            pinterests={pinterests}
            site={site}
            updateSite={updateSite}
            handleAddPinterest={handleAddPinterest}
            callSave={callSave}
          />
        </TabPanel>

        <TabPanel value={'editorial'}>
          <EditorialGuideTab site={site} updateSite={updateSite} callSave={callSave} />
        </TabPanel>

        <TabPanel value={'publication'}>
          <PublicationTimeTab site={site} updateSite={updateSite} />
        </TabPanel>

        <TabPanel value={'visual'}>
          <VisualTab site={site} updateSite={updateSite} userData={userData} />
        </TabPanel>
      </TabContext>
    </Card>
  );
};

export default PinterestSettings;
